import type { LoadedFrom } from '@nowline-app/share';
import { encodeText } from '@nowline-app/share';
import { EMBED_BUNDLE_URL, SHARE_BASE } from './constants.ts';

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Builds a `free.nowline.io/open` share URL for the given source.
 *
 * Emits `#url=<encoded-remote>` only when the diagram was loaded from a
 * remote URL and the editor is unedited (source === loadedFrom.body) —
 * preserving live-link semantics. Any other case uses `#text=`.
 */
export function buildShareUrl(source: string, loadedFrom: LoadedFrom): string {
    const u = new URL(SHARE_BASE);
    if (loadedFrom.kind === 'url' && source === loadedFrom.body) {
        u.hash = `url=${encodeURIComponent(loadedFrom.url)}`;
    } else {
        u.hash = `text=${encodeText(source)}`;
    }
    return u.toString();
}

/**
 * Builds an HTML embed snippet: a `<script>` loader followed by a
 * `<pre><code class="language-nowline">` block with HTML-escaped source.
 * The script pins to the OSS embed's minor channel (auto-rolls patch fixes).
 */
export function buildEmbedSnippet(source: string): string {
    return (
        `<script src="${EMBED_BUNDLE_URL}"></script>\n` +
        `<pre><code class="language-nowline">${escapeHtml(source)}</code></pre>`
    );
}

export async function copyShareLink(source: string, loadedFrom: LoadedFrom): Promise<void> {
    await navigator.clipboard.writeText(buildShareUrl(source, loadedFrom));
}

export async function copyEmbedSnippet(source: string): Promise<void> {
    await navigator.clipboard.writeText(buildEmbedSnippet(source));
}
